import { API, Auth } from 'aws-amplify';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import awsConfig from '@config/aws';
import * as constants from '@config/constants';

API.configure(awsConfig);

const constructPath = (path, useV2) => {
  if (!useV2) {
    return path;
  }

  const api_version = process.env.REACT_APP_API_VERSION;
  switch (api_version) {
    case 'v2':
      return `${path}/${process.env.REACT_APP_CUSTOMER_ID}`;
    default:
      return path;
  }
};

const addParams = (path, params) => {
  if (params) {
    return `${path}?${params}`;
  }
  return path;
};

/**
 * Fires off an API request.
 *
 * @param {string} apiMethod - The HTTP method to use (e.g. 'get', 'post', 'put').
 * @param {string} apiName - The name of the API to use.
 * @param {string} path - The path of the API to use.
 * @param {string} initialTimestamp - The initial timestamp to use for the request.
 * @param {boolean} useBearer - Whether to use bearer authentication.
 * @param {string} params - The parameters to use for the request.
 * @param {boolean} useV2 - Whether to use v2 of the API. Simply denotes a different means of construction of the path. Not relevant for all requests.
 * @param {object} body - The body of the request.
 */
const useAPI = (
  apiMethod,
  apiName,
  path,
  initialTimestamp = '',
  useBearer = false,
  params = '',
  useV2 = true,
  body = {}
) => {
  const dispatch = useDispatch();
  const apiKey = useSelector(state => state.keys.key);

  const screenPath = constructPath(path, useV2);
  const finalPath = addParams(screenPath, params);

  const url = `${apiName}-${finalPath}`;
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  // The parsed response body returned alongside an error status (e.g. a 400
  // that still carries a structured JSON payload such as a rules validation
  // result). Kept separate from `response` (reserved for 2xx bodies) so
  // existing consumers are unaffected, while the rules-diff features can read
  // and render the error body.
  const [errorData, setErrorData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [timestamp, setTimestamp] = useState(initialTimestamp);

  // When the caller clears the request (no path — e.g. resetting its params
  // array to [] after closing a modal), reset the hook's state so a stale
  // response/error doesn't linger into the next use. Existing callers pass a
  // constant, truthy path, so this is a no-op for them.
  useEffect(() => {
    if (!path) {
      setResponse(null);
      setError(null);
      setErrorData(null);
      setIsLoading(false);
    }
  }, [path]);

  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);
      setResponse(null);
      setError(null);
      setErrorData(null);
      useDispatch && dispatch({ type: constants.XHR_IN_PROGRESS, key: url });

      const requestConfig = {
        headers: {
          Authorization: `${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`,
          'x-api-key': apiKey,
        },
      };
      API.get(apiName, finalPath, requestConfig)
        .then(response => {
          setResponse(response);
          setIsLoading(false);
          useDispatch &&
            dispatch({
              type: constants.XHR_COMPLETE,
              key: url,
              payload: response,
            });
        })
        .catch(error => {
          const data = error.response?.data;
          console.error('Error with API:', data?.error || error);
          setIsLoading(false);

          setErrorData(data && typeof data === 'object' ? data : null);
          setError((data?.error || error).toString());
          dispatch({
            type: constants.XHR_COMPLETE,
            key: url,
            payload: { error },
          });
        });
    };
    const fetchData = async apiFn => {
      setIsLoading(true);
      setResponse(null);
      setError(null);
      setErrorData(null);
      useDispatch && dispatch({ type: constants.XHR_IN_PROGRESS, key: url });

      const requestConfig = {
        headers: {
          Authorization: `${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`,
          'x-api-key': apiKey,
          'Content-Type': body?.file
            ? 'multipart/form-data'
            : 'application/json',
        },
        body,
      };
      apiFn(apiName, finalPath, requestConfig)
        .then(response => {
          setResponse(response);
          setIsLoading(false);
          useDispatch &&
            dispatch({
              type: constants.XHR_COMPLETE,
              key: url,
              payload: response,
            });
        })
        .catch(error => {
          const data = error.response?.data;
          console.error('Error with API:', data?.error || error);
          setIsLoading(false);

          setErrorData(data && typeof data === 'object' ? data : null);
          setError((data?.error || error).toString());
          dispatch({
            type: constants.XHR_COMPLETE,
            key: url,
            payload: { error },
          });
        });
    };
    if (path) {
      if (apiMethod === 'get') {
        getData();
      } else if (apiMethod === 'post') {
        fetchData(API.post.bind(API));
      } else if (apiMethod === 'put') {
        fetchData(API.put.bind(API));
      }
    }
    // `initialTimestamp` is included so a caller can force a refetch on an
    // otherwise-unchanged request by passing a fresh value (the params-array
    // pattern stamps a new Date.now() per action). It fires exactly once per
    // change — there is no setTimestamp sync effect that would double-fire.
    // Callers MUST pass a value that is stable across renders (a constant or
    // one held in state); a freshly-computed Date.now() in the argument list
    // would change every render and loop this effect.
    //
    // `body`, `useV2`, and `useBearer` are intentionally omitted. `body`
    // defaults to a new `{}` each render, so including it would change identity
    // every render and re-fire this effect endlessly.
  }, [apiMethod, apiName, path, timestamp, initialTimestamp, apiKey, params]);
  return {
    response,
    invalidateCache: () => setTimestamp(Date.now()),
    error,
    errorData,
    isLoading,
  };
};

export default useAPI;
